import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { PageHero } from '../../components/pageHero/PageHero';
import { Spinner } from 'components/spinner/Spinner';
import { WondeApi } from 'api/wondeApi';
import { UnableToSyncDataPopup } from '../../pages/wondeIntegration/wIPopups/UnableToSyncDataPopup';
import WarningIcon from '../../images/Icon_Warning.png';
import { SyncWondeDataResponse } from './models';
import { SimpleTableSyncError } from './SimpleTableSyncError';

type WondeYearSinkProps = {
  setCurrentStep: Dispatch<SetStateAction<number>>;
  setWondeIntegration: Dispatch<SetStateAction<boolean>>;
  selectedSyncType: string;
  wondeSchoolId: any;
  syncResponse: SyncWondeDataResponse | null;
};


export const WondeYearSink: React.FC<WondeYearSinkProps> = ({ 
  setCurrentStep, 
  setWondeIntegration,
  selectedSyncType,
  wondeSchoolId,
  syncResponse
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [recordData, setRecordData] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [unableToSyncPopupOpen, setUnableToSyncPopupOpen] = useState<boolean>(false);
  const [unableToSyncTitle, setUnableToSyncTitle] = useState<string>("Unable to Sync");
  const [unableToSyncMsg, setUnableToSyncMsg] = useState<string>("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const result = await WondeApi.wondePupilRecords();

    if (!result.error) {
      setRecordData(result.data);
    }
    
    if (result.error) {
      setError("An error occurred, please try again.");
      setIsSubmitting(false);
    }
  };  

  const submitYearSync = async (years_to_sync: string) => {
    const yearSelected = parseInt(years_to_sync.split('/')[0]);

    if(recordData.currentAcademicStartYear > yearSelected){
      setUnableToSyncTitle("Unable to Sync");
      setUnableToSyncMsg("Your MIS has not yet moved to the new academic year. Once your MIS updates to the new academic year, you will be able to sync your data into Get Set 4 Education.");
      setUnableToSyncPopupOpen(true);
    } else if (recordData.currentAcademicStartYear < yearSelected){
      setUnableToSyncTitle("Unable to Sync");
      setUnableToSyncMsg("Woohoo, your MIS has moved to the new academic year. Once you have archived your data in Get Set 4 Education, you will be able to update your data with your Wonde connection.");
      setUnableToSyncPopupOpen(true);
    } else {
      setIsSubmitting(true);

      const result = await WondeApi.submitSelectedWondePupilRecords(wondeSchoolId, selectedSyncType);

      if(result.data && result.data.bNoSyncData){
        setIsSubmitting(false);
        setUnableToSyncTitle("Woohoo!");
        setUnableToSyncMsg("There is no new data to sync. Your Get Set data matches your MIS.");
        setUnableToSyncPopupOpen(true);
      } else {
        if (!result.error) {
          setCurrentStep(1);
        }
        
        if (result.error) {
          setError("An error occurred, please try again.");
          setIsSubmitting(false);
        }  
      }    
    }
  };

  
  return (
    <div className="PageContent WondeIntegrationPage Step1">
      <PageHero title="Calibrate Pupil Records" />
      <div className="WidthContent">
        {isSubmitting &&
          <div className="WILoadingPanel">
            <Spinner color={'#f59e16'} />
          </div>
        }

        <SimpleTableSyncError
          title='Failed to import all pupils'
          errorMsg="Failed to import all pupils. Please contact Get Set on admin@getset4education.co.uk for further support."
          data={syncResponse?.nonConformingPupils ?? []}
        />

        {!recordData || recordData.samplePupils.length > 0 ?
          <>
            <div className="TopInfoRow">
              <div className="LeftSide">
                <h2>Year Syncing</h2>
              </div>
              <div className="RightSide"></div>
            </div>

            {recordData && !error &&
              <p>Your Get Set 4 Education is in {recordData.currentAcademicStartYear}/{recordData.currentAcademicStartYear + 1}. Before we can sync, we need to confirm what academic year your registration system is in. Here are five pupil records we have pulled from Wonde:</p>
            }

            <div className="LineSpacer3"></div>

            {error &&
              <div className="ErrorTxt">{error}</div>
            }

            <div className="LineSpacer4"></div>

            {!recordData && !error &&
              <div style={{ textAlign: "center", marginBottom: "40px" }}>
                <Spinner color={'#f59e16'} />
              </div>
            }

            {recordData && !error &&
              <>
                <h4>Wonde Records</h4>

                <div className="InfoTableContainer StaticTable">
                  <div className="LTContainer">
                    <div className="InfoTable HideChkboxCol">
                      <div className="MiddleTable">
                        <div className="Head">
                          <div className="Cell CheckboxCell"></div>
                          <div className="Cell"><span className="Txt">Name</span></div>
                          <div className="Cell"><span className="Txt">ID</span></div>
                          <div className="Cell"><span className="Txt">Class</span></div>
                          <div className="Cell"><span className="Txt">Wonde Year</span></div>
                        </div>
                        <div className="TableBody">
                          {recordData.samplePupils.map((item: any, index: number) => {
                            return (
                              <div className="Row" key={index}>
                                <div className="Cell CheckboxCell"></div>
                                <div className="Cell"><span className="Txt">{item.fullName}</span></div>
                                <div className="Cell"><span className="Txt">{item.wondeId}</span></div>
                                <div className="Cell"><span className="Txt">{item.wondeClassName}</span></div>
                                <div className="Cell"><span className="Txt">{item.wondeApplicableYears}</span></div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <p>In what academic year were these pupils in this year group?</p>

                <div className="BtmBtns FullWidth">
                  <div className="Btn BtnWhite LeftBtn" onClick={() => {
                    window.scrollTo(0, 0);
                    setWondeIntegration(false);
                  }}>Cancel</div>
                  <div className="Btn BtnOrange RightBtn" onClick={() => submitYearSync(recordData.currentAcademicYear)}>{recordData.currentAcademicYear}</div>
                  <div className="Btn BtnOrange RightBtn" onClick={() => submitYearSync(recordData.nextAcademicYear)}>{recordData.nextAcademicYear}</div>
                </div>
              </>
            }

            {error &&
              <div className="BtmBtns">
                <div className="Btn BtnWhite LeftBtn" onClick={() => {
                  window.scrollTo(0, 0);
                  setWondeIntegration(false);
                }}>Cancel</div>
              </div>
            } 
          </>
        :
          <>
            <div className="NoWondeDataError">
              <img src={WarningIcon} alt="No Wonde data error" />
              <h2>It looks like you don't have any data in Wonde. Add your school data into Wonde, then we'll be able to sync your records!</h2>
              <div className="BtmBtns">
                <div className="Btn BtnWhite LeftBtn" onClick={() => {
                  window.scrollTo(0, 0);
                  setWondeIntegration(false);
                }}>Back to Manage School</div>
              </div>              
            </div>
          </>
        }       
      </div>

      {unableToSyncPopupOpen && (
        <UnableToSyncDataPopup
          panelOpen={unableToSyncPopupOpen}
          backToProfilePage={() => {
            window.scrollTo(0, 0);
            setWondeIntegration(false);
          }}
          unableToSyncTitle={unableToSyncTitle}
          unableToSyncMsg={unableToSyncMsg}
        />        
      )}       
    </div>
  );
};